/**
 * Centralized storage adapter with workd: prefix naming convention.
 * All session-related data uses this adapter for consistent key management
 * and centralized cleanup on logout.
 *
 * Key format: workd:{namespace}:{key}
 * Examples:
 *   - workd:auth:state
 *   - workd:list:counts:clients
 *   - workd:cart:modified:123
 */

import type { StateStorage } from 'zustand/middleware';
import { createJSONStorage } from 'zustand/middleware';

const WORKD_PREFIX = 'workd:';

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable(): boolean {
	if (typeof window === 'undefined' || window.localStorage == null) {
		return false;
	}

	try {
		const testKey = '__workd_test__';
		window.localStorage.setItem(testKey, testKey);
		window.localStorage.removeItem(testKey);

		return true;
	} catch {
		return false;
	}
}

/**
 * Check if sessionStorage is available
 */
function isSessionStorageAvailable(): boolean {
	if (typeof window === 'undefined' || window.sessionStorage == null) {
		return false;
	}

	try {
		const testKey = '__workd_test__';
		window.sessionStorage.setItem(testKey, testKey);
		window.sessionStorage.removeItem(testKey);

		return true;
	} catch {
		return false;
	}
}

/**
 * Custom StateStorage implementation for Zustand persist middleware.
 * Uses localStorage with proper error handling.
 */
const workdStateStorage: StateStorage = {
	getItem: (name: string): string | null => {
		if (!isLocalStorageAvailable()) {
			return null;
		}

		return localStorage.getItem(name);
	},

	setItem: (name: string, value: string): void => {
		if (!isLocalStorageAvailable()) {
			return;
		}

		localStorage.setItem(name, value);
	},

	removeItem: (name: string): void => {
		if (!isLocalStorageAvailable()) {
			return;
		}

		localStorage.removeItem(name);
	},
};

/**
 * Zustand persist storage adapter.
 * Uses createJSONStorage for proper serialization/deserialization.
 */
export const workdStorage = createJSONStorage(() => workdStateStorage);

/**
 * Get a value from workd: namespaced localStorage
 */
export function getWorkdItem<T>(namespace: string, key: string): T | null {
	if (!isLocalStorageAvailable()) {
		return null;
	}

	try {
		const fullKey = `${WORKD_PREFIX}${namespace}:${key}`;
		const value = localStorage.getItem(fullKey);

		if (value == null) {
			return null;
		}

		return JSON.parse(value) as T;
	} catch {
		return null;
	}
}

/**
 * Set a value in workd: namespaced localStorage
 */
export function setWorkdItem<T>(
	namespace: string,
	key: string,
	value: T,
): void {
	if (!isLocalStorageAvailable()) {
		return;
	}

	try {
		const fullKey = `${WORKD_PREFIX}${namespace}:${key}`;
		localStorage.setItem(fullKey, JSON.stringify(value));
	} catch {
		// Silently fail if localStorage is full or unavailable
	}
}

/**
 * Remove a value from workd: namespaced localStorage
 */
export function removeWorkdItem(namespace: string, key: string): void {
	if (!isLocalStorageAvailable()) {
		return;
	}

	try {
		const fullKey = `${WORKD_PREFIX}${namespace}:${key}`;
		localStorage.removeItem(fullKey);
	} catch {
		// Silently fail
	}
}

/**
 * Clear all workd: prefixed keys from localStorage.
 * Called on logout to ensure complete session cleanup.
 */
export function clearAllWorkdLocalStorage(): void {
	if (!isLocalStorageAvailable()) {
		return;
	}

	console.debug(
		'[workdStorage] clearAllWorkdLocalStorage - CLEARING ALL WORKD KEYS',
	);
	console.trace('[workdStorage] clearAllWorkdLocalStorage - stack trace');

	try {
		const keys = Object.keys(localStorage);

		keys.forEach((key) => {
			if (key.startsWith(WORKD_PREFIX)) {
				console.debug(
					'[workdStorage] clearAllWorkdLocalStorage - removing key:',
					key,
				);
				localStorage.removeItem(key);
			}
		});
	} catch {
		// Silently fail
	}
}

/**
 * Clear all workd: prefixed keys from sessionStorage.
 * Called on logout to ensure complete session cleanup.
 */
export function clearAllWorkdSessionStorage(): void {
	if (!isSessionStorageAvailable()) {
		return;
	}

	try {
		const keys = Object.keys(sessionStorage);

		keys.forEach((key) => {
			if (key.startsWith(WORKD_PREFIX)) {
				sessionStorage.removeItem(key);
			}
		});
	} catch {
		// Silently fail
	}
}

/**
 * Clear ALL workd: prefixed keys from both localStorage and sessionStorage.
 * This is the primary cleanup function called on logout.
 */
export function clearAllWorkdStorage(): void {
	clearAllWorkdLocalStorage();
	clearAllWorkdSessionStorage();
}

/**
 * Get the full workd: prefixed key for a namespace and key
 */
export function getWorkdKey(namespace: string, key: string): string {
	return `${WORKD_PREFIX}${namespace}:${key}`;
}

/**
 * Check if a key is a workd: prefixed key
 */
export function isWorkdKey(key: string): boolean {
	return key.startsWith(WORKD_PREFIX);
}
